import {
  useGetProposalFinancials,
  useGetProposalPaymentSchedule,
} from '@/api/queries/proposal/useGetProposal';
import { ErrorBoundary } from '@/ui/components/errorBoundary';
import GrantScheduleNote from '@/ui/components/grantScheduleNote/GrantScheduleNote';
import { ProjectAccountingIntegrationCategoriesControl } from '@/ui/components/projectAccountingIntegrationCategoriesControl/ProjectAccountingIntegrationCategoriesControl';
import ProposalPaymentOptions, {
  ProposalPaymentOptions_Account,
  ProposalPaymentOptions_Project,
} from '@/ui/components/proposalPaymentOptions/ProposalPaymentOptions';
import ProposalPaymentScheduleForm from '@/ui/components/proposalPaymentScheduleForm/ProposalPaymentScheduleForm';
import PriceDetailOptions, {
  PriceDetailOptions_Project,
} from '@/ui/components/priceDetailOptions/PriceDetailOptions';
import ProposalSummaryAlert from '@/ui/components/proposalSummaryAlert/ProposalSummaryAlert';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import RequestDepositButton from '@/ui/pages/proposalBuilderPage/components/RequestDepositButton';
import { ResultOf } from '@graphql-typed-document-node/core';
import { DividerWithLabel, Text } from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';

export type TProps = {
  proposalId: string;
  /** Public id of the Project the Proposal belongs to. */
  projectId: string;
  /** Proposal total in minor units, inc tax. */
  proposalTotal: number;
  /** Project data (from the page's root query) supplying option values. */
  project?: ResultOf<typeof ProposalPaymentOptions_Project> &
    ResultOf<typeof PriceDetailOptions_Project>;
  /** Account data (from the page's account-settings query) for option gating. */
  account?: ResultOf<typeof ProposalPaymentOptions_Account>;
};

const PaymentTab: FC<TProps> = (props) => {
  const { proposalId, projectId, proposalTotal, project, account } = props;
  const queryClient = useQueryClient();

  const { paymentSchedule, isPending } =
    useGetProposalPaymentSchedule(proposalId);
  const { grants } = useGetProposalFinancials(proposalId);
  const attachedGrants = grants ?? [];

  // Gate on load so react-hook-form seeds its defaults from the fetched
  // schedule rather than an empty placeholder it would never update from.
  if (isPending) return null;

  return (
    <div className="flex flex-col gap-6 p-3.5">
      <div className="flex flex-col gap-3">
        {/* A deposit and a payment schedule are mutually exclusive — once a
            schedule is in place the deposit control no longer applies. */}
        {!paymentSchedule && (
          <>
            <RequestDepositButton proposalId={proposalId} />

            <Text as="div">
              <DividerWithLabel label="or" />
            </Text>
          </>
        )}

        {/* The stages below are denominated on the gross, and a grant never
            rewrites them. Without this the two panels read as disagreeing. */}
        {paymentSchedule && attachedGrants.length > 0 && (
          <GrantScheduleNote
            grantNames={attachedGrants.map((grant) => grant.name)}
          />
        )}

        <ProposalPaymentScheduleForm
          proposalId={proposalId}
          proposalTotal={proposalTotal}
          paymentSchedule={paymentSchedule ?? { stages: [], explainer: null }}
        />
      </div>

      <ProposalSummaryAlert proposalId={proposalId} />

      <PriceDetailOptions
        projectId={projectId}
        project={project}
        onSaved={() =>
          void queryClient.invalidateQueries({
            queryKey: proposalKeys.proposal(proposalId),
          })
        }
      />

      <ProposalPaymentOptions
        projectId={projectId}
        project={project}
        account={account}
      />

      <ErrorBoundary>
        <ProjectAccountingIntegrationCategoriesControl projectId={projectId} />
      </ErrorBoundary>
    </div>
  );
};

export default PaymentTab;
